<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Base Tools</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Built-in tools available to your organization
        </p>
      </div>
      <div class="w-64">
        <Input v-model="searchQuery" placeholder="Search tools..." class="w-full" />
      </div>
    </div>

    <div v-if="store.isLoadingBaseTools" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <Alert v-else-if="store.baseToolsError" variant="error">
      {{ store.baseToolsError }}
    </Alert>

    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="filteredTools.length === 0"
      class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ searchQuery ? 'No tools match your search' : 'No base tools available' }}
      </p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <ToolTile
        v-for="tool in filteredTools"
        :key="tool.id"
        :tool="tool"
        :is-base="true"
        :is-enabled="store.enabledToolIds.has(tool.id)"
        @toggle-enable="handleToggle(tool)"
        @view-detail="emit('view-detail', tool)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Alert, Input, LoadingSpinner } from '@asabdev/asab-ui';
  import { useMcpToolsStore } from '@/stores/mcpTools';
  import type { BaseTool } from '@/lib/mcp/types';
  import ToolTile from './ToolTile.vue';

  interface Props {
    orgId: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'view-detail', tool: BaseTool): void;
  }>();

  const store = useMcpToolsStore();
  const searchQuery = ref('');

  const filteredTools = computed(() => {
    const query = searchQuery.value.toLowerCase().trim();
    if (!query) return store.baseTools;
    return store.baseTools.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query),
    );
  });

  const handleToggle = async (tool: BaseTool) => {
    if (store.enabledToolIds.has(tool.id)) {
      await store.disableTool(props.orgId, tool.id);
    } else {
      await store.enableTool(props.orgId, tool.id, 'base');
    }
  };
</script>
